import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

export interface TourPageFaq {
  id: string;
  question: string;
  answer: string;
  sort_order: number;
}

interface TourPageFaqRow extends TourPageFaq {
  question_vi: string | null;
  answer_vi: string | null;
}

export function useTourPageFaqs() {
  const [rows, setRows] = useState<TourPageFaqRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { language } = useLanguage();

  useEffect(() => {
    let cancelled = false;

    async function fetchFaqs() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
          .from('vietnamtravel_tour_page_faqs')
          .select('id, question, question_vi, answer, answer_vi, sort_order')
          .order('sort_order', { ascending: true });

        if (err) throw err;
        if (!cancelled) setRows((data ?? []) as TourPageFaqRow[]);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load FAQs');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchFaqs();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const faqs: TourPageFaq[] = rows.map((r) => ({
    id: r.id,
    question: loc(language, r.question, r.question_vi),
    answer: loc(language, r.answer ?? '', r.answer_vi),
    sort_order: r.sort_order ?? 0,
  }));

  return { faqs, loading, error, refresh };
}